export type CodeLanguage =
  'python' | 'javascript' | 'csharp' | 'curl' | 'java' | 'go';

export const CODE_LANGUAGES: CodeLanguage[] = [
  'python',
  'javascript',
  'csharp',
  'curl',
  'java',
  'go',
];

export type SnippetKind = 'webhook' | 'api-push';

// What an API push sends. Each has its own endpoint and payload; webhooks
// only ever send observations.
export type ApiPushDataType = 'observations' | 'events' | 'attachments';

export type SnippetParams = {
  endpoint: string;
  apiKey: string | null;
  integrationType: string;
  kind: SnippetKind;
  // API push only. For 'attachments', endpoint is the events endpoint: the
  // sample creates the event there, then posts files to its attachments path.
  dataType?: ApiPushDataType;
};

const API_KEY_PLACEHOLDER = '<YOUR_API_KEY>';

const resolveKey = (apiKey: string | null) => apiKey ?? API_KEY_PLACEHOLDER;

const sourceFieldName = (kind: SnippetKind) =>
  kind === 'webhook' ? 'source_id' : 'source';

const LANGUAGE_LABELS = {
  python: 'Python',
  javascript: 'JavaScript',
  csharp: 'C#',
  curl: 'curl',
  java: 'Java',
  go: 'Go',
} as const;

export type LanguageLabel = (typeof LANGUAGE_LABELS)[CodeLanguage];

export const languageLabel = (language: CodeLanguage): LanguageLabel =>
  LANGUAGE_LABELS[language];

const pythonSnippet = ({
  endpoint,
  apiKey,
  integrationType,
  kind,
}: SnippetParams) => {
  const key = resolveKey(apiKey);
  const source = sourceFieldName(kind);
  const headers =
    kind === 'webhook'
      ? `headers={\n        "apikey": "${key}",\n        "x-gundi-integration-type": "${integrationType}",\n    }`
      : `headers={"apikey": "${key}"}`;
  return `import requests

requests.post(
    "${endpoint}",
    ${headers},
    json={
        "${source}": "device-001",
        "recorded_at": "2024-01-15T10:30:00+00:00",
        "location": {"lat": -1.2921, "lon": 36.8219},
    },
)
`;
};

const javascriptSnippet = ({
  endpoint,
  apiKey,
  integrationType,
  kind,
}: SnippetParams) => {
  const key = resolveKey(apiKey);
  const source = sourceFieldName(kind);
  const headerLines = [
    `            "apikey": "${key}",`,
    ...(kind === 'webhook'
      ? [`            "x-gundi-integration-type": "${integrationType}",`]
      : []),
    `            "Content-Type": "application/json",`,
  ].join('\n');
  return `async function sendObservation() {
    await fetch("${endpoint}", {
        method: "POST",
        headers: {
${headerLines}
        },
        body: JSON.stringify({
            ${source}: "device-001",
            recorded_at: "2024-01-15T10:30:00+00:00",
            location: { lat: -1.2921, lon: 36.8219 },
        }),
    });
}

sendObservation();
`;
};

const csharpSnippet = ({
  endpoint,
  apiKey,
  integrationType,
  kind,
}: SnippetParams) => {
  const key = resolveKey(apiKey);
  const source = sourceFieldName(kind);
  const extraHeader =
    kind === 'webhook'
      ? `client.DefaultRequestHeaders.Add("x-gundi-integration-type", "${integrationType}");\n`
      : '';
  return `using System.Net.Http;
using System.Text;
using System.Text.Json;

using var client = new HttpClient();
client.DefaultRequestHeaders.Add("apikey", "${key}");
${extraHeader}
var payload = new
{
    ${source} = "device-001",
    recorded_at = "2024-01-15T10:30:00+00:00",
    location = new { lat = -1.2921, lon = 36.8219 },
};

var content = new StringContent(
    JsonSerializer.Serialize(payload),
    Encoding.UTF8,
    "application/json"
);

await client.PostAsync("${endpoint}", content);
`;
};

const curlSnippet = ({
  endpoint,
  apiKey,
  integrationType,
  kind,
}: SnippetParams) => {
  const key = resolveKey(apiKey);
  const source = sourceFieldName(kind);
  const headerLines = [
    `  -H "apikey: ${key}" \\`,
    ...(kind === 'webhook'
      ? [`  -H "x-gundi-integration-type: ${integrationType}" \\`]
      : []),
    `  -H "Content-Type: application/json" \\`,
  ].join('\n');
  return `curl -X POST "${endpoint}" \\
${headerLines}
  -d '{"${source}": "device-001", "recorded_at": "2024-01-15T10:30:00+00:00", "location": {"lat": -1.2921, "lon": 36.8219}}'
`;
};

const javaSnippet = ({
  endpoint,
  apiKey,
  integrationType,
  kind,
}: SnippetParams) => {
  const key = resolveKey(apiKey);
  const source = sourceFieldName(kind);
  const extraHeader =
    kind === 'webhook'
      ? `    .header("x-gundi-integration-type", "${integrationType}")\n`
      : '';
  return `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;

var client = HttpClient.newHttpClient();
var body = """
    {
      "${source}": "device-001",
      "recorded_at": "2024-01-15T10:30:00+00:00",
      "location": {"lat": -1.2921, "lon": 36.8219}
    }
    """;

var request = HttpRequest.newBuilder()
    .uri(URI.create("${endpoint}"))
    .header("apikey", "${key}")
${extraHeader}    .header("Content-Type", "application/json")
    .POST(HttpRequest.BodyPublishers.ofString(body))
    .build();

client.send(request, HttpResponse.BodyHandlers.ofString());
`;
};

const goSnippet = ({
  endpoint,
  apiKey,
  integrationType,
  kind,
}: SnippetParams) => {
  const key = resolveKey(apiKey);
  const source = sourceFieldName(kind);
  const extraHeader =
    kind === 'webhook'
      ? `    req.Header.Set("x-gundi-integration-type", "${integrationType}")\n`
      : '';
  return `package main

import (
    "bytes"
    "net/http"
)

func main() {
    payload := []byte(\`{"${source}": "device-001", "recorded_at": "2024-01-15T10:30:00+00:00", "location": {"lat": -1.2921, "lon": 36.8219}}\`)

    req, _ := http.NewRequest("POST", "${endpoint}", bytes.NewBuffer(payload))
    req.Header.Set("apikey", "${key}")
${extraHeader}    req.Header.Set("Content-Type", "application/json")

    client := &http.Client{}
    client.Do(req)
}
`;
};

export const SNIPPET_GENERATORS: Record<
  CodeLanguage,
  (params: SnippetParams) => string
> = {
  python: pythonSnippet,
  javascript: javascriptSnippet,
  csharp: csharpSnippet,
  curl: curlSnippet,
  java: javaSnippet,
  go: goSnippet,
};

const EVENT_FIELDS = {
  event_type: 'wildlife_sighting_rep',
  title: 'Wildlife sighting',
  recorded_at: '2024-01-15T10:30:00+00:00',
};

const pythonEventSnippet = ({
  endpoint,
  apiKey,
}: SnippetParams) => `import requests

requests.post(
    "${endpoint}",
    headers={"apikey": "${resolveKey(apiKey)}"},
    json={
        "event_type": "${EVENT_FIELDS.event_type}",
        "title": "${EVENT_FIELDS.title}",
        "recorded_at": "${EVENT_FIELDS.recorded_at}",
        "location": {"lat": -1.2921, "lon": 36.8219},
        "event_details": {"species": "elephant", "count": 12},
    },
)
`;

const javascriptEventSnippet = ({
  endpoint,
  apiKey,
}: SnippetParams) => `async function sendEvent() {
    await fetch("${endpoint}", {
        method: "POST",
        headers: {
            "apikey": "${resolveKey(apiKey)}",
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            event_type: "${EVENT_FIELDS.event_type}",
            title: "${EVENT_FIELDS.title}",
            recorded_at: "${EVENT_FIELDS.recorded_at}",
            location: { lat: -1.2921, lon: 36.8219 },
            event_details: { species: "elephant", count: 12 },
        }),
    });
}

sendEvent();
`;

const csharpEventSnippet = ({
  endpoint,
  apiKey,
}: SnippetParams) => `using System.Net.Http;
using System.Text;
using System.Text.Json;

using var client = new HttpClient();
client.DefaultRequestHeaders.Add("apikey", "${resolveKey(apiKey)}");

var payload = new
{
    event_type = "${EVENT_FIELDS.event_type}",
    title = "${EVENT_FIELDS.title}",
    recorded_at = "${EVENT_FIELDS.recorded_at}",
    location = new { lat = -1.2921, lon = 36.8219 },
    event_details = new { species = "elephant", count = 12 },
};

var content = new StringContent(
    JsonSerializer.Serialize(payload),
    Encoding.UTF8,
    "application/json"
);

await client.PostAsync("${endpoint}", content);
`;

const curlEventSnippet = ({
  endpoint,
  apiKey,
}: SnippetParams) => `curl -X POST "${endpoint}" \\
  -H "apikey: ${resolveKey(apiKey)}" \\
  -H "Content-Type: application/json" \\
  -d '{"event_type": "${EVENT_FIELDS.event_type}", "title": "${EVENT_FIELDS.title}", "recorded_at": "${EVENT_FIELDS.recorded_at}", "location": {"lat": -1.2921, "lon": 36.8219}, "event_details": {"species": "elephant", "count": 12}}'
`;

const javaEventSnippet = ({
  endpoint,
  apiKey,
}: SnippetParams) => `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;

var client = HttpClient.newHttpClient();
var body = """
    {
      "event_type": "${EVENT_FIELDS.event_type}",
      "title": "${EVENT_FIELDS.title}",
      "recorded_at": "${EVENT_FIELDS.recorded_at}",
      "location": {"lat": -1.2921, "lon": 36.8219},
      "event_details": {"species": "elephant", "count": 12}
    }
    """;

var request = HttpRequest.newBuilder()
    .uri(URI.create("${endpoint}"))
    .header("apikey", "${resolveKey(apiKey)}")
    .header("Content-Type", "application/json")
    .POST(HttpRequest.BodyPublishers.ofString(body))
    .build();

client.send(request, HttpResponse.BodyHandlers.ofString());
`;

const goEventSnippet = ({ endpoint, apiKey }: SnippetParams) => `package main

import (
    "bytes"
    "net/http"
)

func main() {
    payload := []byte(\`{"event_type": "${EVENT_FIELDS.event_type}", "title": "${EVENT_FIELDS.title}", "recorded_at": "${EVENT_FIELDS.recorded_at}", "location": {"lat": -1.2921, "lon": 36.8219}, "event_details": {"species": "elephant", "count": 12}}\`)

    req, _ := http.NewRequest("POST", "${endpoint}", bytes.NewBuffer(payload))
    req.Header.Set("apikey", "${resolveKey(apiKey)}")
    req.Header.Set("Content-Type", "application/json")

    client := &http.Client{}
    client.Do(req)
}
`;

const pythonAttachmentSnippet = ({
  endpoint,
  apiKey,
}: SnippetParams) => `import requests

headers = {"apikey": "${resolveKey(apiKey)}"}

# 1. Create the event
event = requests.post(
    "${endpoint}",
    headers=headers,
    json={
        "event_type": "${EVENT_FIELDS.event_type}",
        "title": "${EVENT_FIELDS.title}",
        "recorded_at": "${EVENT_FIELDS.recorded_at}",
        "location": {"lat": -1.2921, "lon": 36.8219},
    },
).json()

# 2. Add files to the event
with open("photo.jpg", "rb") as photo:
    requests.post(
        f"${endpoint}{event['object_id']}/attachments/",
        headers=headers,
        files={"file1": photo},
    )
`;

const javascriptAttachmentSnippet = ({
  endpoint,
  apiKey,
}: SnippetParams) => `import { readFile } from "node:fs/promises";

async function sendEventWithAttachment() {
    // 1. Create the event
    const response = await fetch("${endpoint}", {
        method: "POST",
        headers: {
            "apikey": "${resolveKey(apiKey)}",
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            event_type: "${EVENT_FIELDS.event_type}",
            title: "${EVENT_FIELDS.title}",
            recorded_at: "${EVENT_FIELDS.recorded_at}",
            location: { lat: -1.2921, lon: 36.8219 },
        }),
    });
    const event = await response.json();

    // 2. Add files to the event
    const form = new FormData();
    form.append("file1", new Blob([await readFile("photo.jpg")]), "photo.jpg");
    await fetch(\`${endpoint}\${event.object_id}/attachments/\`, {
        method: "POST",
        headers: { "apikey": "${resolveKey(apiKey)}" },
        body: form,
    });
}

sendEventWithAttachment();
`;

const csharpAttachmentSnippet = ({
  endpoint,
  apiKey,
}: SnippetParams) => `using System.IO;
using System.Net.Http;
using System.Text;
using System.Text.Json;

using var client = new HttpClient();
client.DefaultRequestHeaders.Add("apikey", "${resolveKey(apiKey)}");

// 1. Create the event
var payload = new
{
    event_type = "${EVENT_FIELDS.event_type}",
    title = "${EVENT_FIELDS.title}",
    recorded_at = "${EVENT_FIELDS.recorded_at}",
    location = new { lat = -1.2921, lon = 36.8219 },
};
var response = await client.PostAsync(
    "${endpoint}",
    new StringContent(JsonSerializer.Serialize(payload), Encoding.UTF8, "application/json")
);
using var created = JsonDocument.Parse(await response.Content.ReadAsStringAsync());
var objectId = created.RootElement.GetProperty("object_id").GetString();

// 2. Add files to the event
using var form = new MultipartFormDataContent();
form.Add(new ByteArrayContent(File.ReadAllBytes("photo.jpg")), "file1", "photo.jpg");
await client.PostAsync($"${endpoint}{objectId}/attachments/", form);
`;

const curlAttachmentSnippet = ({
  endpoint,
  apiKey,
}: SnippetParams) => `# 1. Create the event. The response includes its object_id.
curl -X POST "${endpoint}" \\
  -H "apikey: ${resolveKey(apiKey)}" \\
  -H "Content-Type: application/json" \\
  -d '{"event_type": "${EVENT_FIELDS.event_type}", "title": "${EVENT_FIELDS.title}", "recorded_at": "${EVENT_FIELDS.recorded_at}", "location": {"lat": -1.2921, "lon": 36.8219}}'

# 2. Add files to the event
curl -X POST "${endpoint}<OBJECT_ID>/attachments/" \\
  -H "apikey: ${resolveKey(apiKey)}" \\
  -F "file1=@photo.jpg"
`;

const javaAttachmentSnippet = ({
  endpoint,
  apiKey,
}: SnippetParams) => `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import java.nio.file.Files;
import java.nio.file.Path;
import java.util.List;

var client = HttpClient.newHttpClient();

// 1. Create the event
var body = """
    {
      "event_type": "${EVENT_FIELDS.event_type}",
      "title": "${EVENT_FIELDS.title}",
      "recorded_at": "${EVENT_FIELDS.recorded_at}",
      "location": {"lat": -1.2921, "lon": 36.8219}
    }
    """;
var create = HttpRequest.newBuilder()
    .uri(URI.create("${endpoint}"))
    .header("apikey", "${resolveKey(apiKey)}")
    .header("Content-Type", "application/json")
    .POST(HttpRequest.BodyPublishers.ofString(body))
    .build();
var created = client.send(create, HttpResponse.BodyHandlers.ofString());
var objectId = created.body().replaceAll(".*\\"object_id\\"\\\\s*:\\\\s*\\"([^\\"]+)\\".*", "$1");

// 2. Add files to the event
var boundary = "gundi-boundary";
var head = ("--" + boundary + "\\r\\n"
    + "Content-Disposition: form-data; name=\\"file1\\"; filename=\\"photo.jpg\\"\\r\\n"
    + "Content-Type: image/jpeg\\r\\n\\r\\n").getBytes();
var tail = ("\\r\\n--" + boundary + "--\\r\\n").getBytes();
var upload = HttpRequest.newBuilder()
    .uri(URI.create("${endpoint}" + objectId + "/attachments/"))
    .header("apikey", "${resolveKey(apiKey)}")
    .header("Content-Type", "multipart/form-data; boundary=" + boundary)
    .POST(HttpRequest.BodyPublishers.ofByteArrays(
        List.of(head, Files.readAllBytes(Path.of("photo.jpg")), tail)))
    .build();

client.send(upload, HttpResponse.BodyHandlers.ofString());
`;

const goAttachmentSnippet = ({
  endpoint,
  apiKey,
}: SnippetParams) => `package main

import (
    "bytes"
    "encoding/json"
    "io"
    "mime/multipart"
    "net/http"
    "os"
)

func main() {
    client := &http.Client{}

    // 1. Create the event
    payload := []byte(\`{"event_type": "${EVENT_FIELDS.event_type}", "title": "${EVENT_FIELDS.title}", "recorded_at": "${EVENT_FIELDS.recorded_at}", "location": {"lat": -1.2921, "lon": 36.8219}}\`)
    req, _ := http.NewRequest("POST", "${endpoint}", bytes.NewBuffer(payload))
    req.Header.Set("apikey", "${resolveKey(apiKey)}")
    req.Header.Set("Content-Type", "application/json")
    resp, _ := client.Do(req)
    var event struct {
        ObjectID string \`json:"object_id"\`
    }
    json.NewDecoder(resp.Body).Decode(&event)

    // 2. Add files to the event
    var form bytes.Buffer
    writer := multipart.NewWriter(&form)
    part, _ := writer.CreateFormFile("file1", "photo.jpg")
    photo, _ := os.Open("photo.jpg")
    io.Copy(part, photo)
    writer.Close()

    upload, _ := http.NewRequest("POST", "${endpoint}"+event.ObjectID+"/attachments/", &form)
    upload.Header.Set("apikey", "${resolveKey(apiKey)}")
    upload.Header.Set("Content-Type", writer.FormDataContentType())
    client.Do(upload)
}
`;

const EVENT_GENERATORS: Record<
  CodeLanguage,
  (params: SnippetParams) => string
> = {
  python: pythonEventSnippet,
  javascript: javascriptEventSnippet,
  csharp: csharpEventSnippet,
  curl: curlEventSnippet,
  java: javaEventSnippet,
  go: goEventSnippet,
};

const ATTACHMENT_GENERATORS: Record<
  CodeLanguage,
  (params: SnippetParams) => string
> = {
  python: pythonAttachmentSnippet,
  javascript: javascriptAttachmentSnippet,
  csharp: csharpAttachmentSnippet,
  curl: curlAttachmentSnippet,
  java: javaAttachmentSnippet,
  go: goAttachmentSnippet,
};

export const generateSnippet = (
  language: CodeLanguage,
  params: SnippetParams,
) => {
  if (params.kind === 'api-push' && params.dataType === 'events')
    return EVENT_GENERATORS[language](params);
  if (params.kind === 'api-push' && params.dataType === 'attachments')
    return ATTACHMENT_GENERATORS[language](params);
  return SNIPPET_GENERATORS[language](params);
};
